import { APP_DISPLAY_NAME, GQL, sleep } from "platform-common/index"
import { trackAnalyticsEvent } from "@web/context/analytics/client"
import cs from "classnames"
import CheckIcon from "@mui/icons-material/Check"
import styles from "./PricingPlansNew.module.scss"
import React from "react"
import { useBillingContext } from "@web/context/BillingContext"
import { useBilling } from "@web/hooks/useBilling"
import { CircularProgress } from "@mui/material"
import { CALENDLY_LINK } from "platform-common/util/contact"

export const getBillingProductDescription = (description: string | undefined | null) => {
  if (!description) return null
  if (typeof description !== "string") return description
  const splitDescription = description.split("\\n")
  return (
    <>
      {splitDescription[0]}
      {splitDescription[1] ? (
        <>
          <br />
          {splitDescription[1]}
        </>
      ) : null}
    </>
  )
}

type BillingProduct = GQL.BillingProductFragmentFragment

export const BillingProductCard = ({ product, highlighted }: { product: BillingProduct; highlighted?: boolean }) => {
  const { billingStatus, getBillingStatus } = useBillingContext()
  const { redirectToCheckout, modifySubscription, redirectToCheckoutLoading, modifySubscriptionLoading } = useBilling()
  const [isModifying, setIsModifying] = React.useState(false)

  const isLoading = isModifying || redirectToCheckoutLoading || modifySubscriptionLoading

  const price = {
    value: Math.floor((product.prices[0]?.unitAmountCents || 0) / 100),
    prefix: "",
    suffix: product.accountType === "ENTERPRISE" ? "/Company/Month" : "/User/Month",
  }

  const isPaidPlan = price.value !== 0

  const isEnterprise = product.accountType === "ENTERPRISE"
  const buyLabel = isEnterprise ? `Schedule Onboarding` : `Get Started`

  const selectPlan = React.useCallback(
    async (priceId: string | undefined) => {
      if (!priceId) return
      if (isEnterprise) {
        window.open(CALENDLY_LINK, "_blank")
        return
      }
      if (isLoading) {
        return
      }

      trackAnalyticsEvent({
        event: "Buy Button Clicked",
        product: product.name,
        isNewSubscription: Boolean(billingStatus?.getBillingStatus.isSubscriptionActive),
      })
      // if we have an active subscription, modify it
      // otherwise, create a new subscription
      if (billingStatus?.getBillingStatus.isSubscriptionActive) {
        setIsModifying(true)
        try {
          await modifySubscription({
            variables: {
              input: {
                priceId,
              },
            },
          })

          // wait for the subscription to be modified
          // TODO: would be nice to subscribe to the subscription status
          await sleep(4000)

          // get the updated billing status
          getBillingStatus()
        } finally {
          setIsModifying(false)
        }
      } else {
        redirectToCheckout(priceId).catch(() => {})
      }
    },
    [
      isEnterprise,
      isLoading,
      billingStatus?.getBillingStatus.isSubscriptionActive,
      modifySubscription,
      getBillingStatus,
      redirectToCheckout,
    ]
  )

  return (
    <div className={cs("tmb-mr-4 tmb-ml-4", styles.card, { [styles.highlighted]: highlighted })}>
      <div className="tmb-flex tmb-items-center tmb-justify-between tmb-gap-x-4">
        <h3
          id={product.name}
          className={cs(
            highlighted ? "tmb-text-mainBlue" : "tmb-text-gray-900",
            "tmb-text-xl tmb-font-semibold tmb-leading-8 tmb-m-0"
          )}
        >
          {product.name}
        </h3>
      </div>
      <p className="tmb-mt-4 tmb-text-sm tmb-leading-6 tmb-text-gray-600">
        {getBillingProductDescription(product.description)}
      </p>
      <div className="tmb-flex tmb-flex-col tmb-mt-4">
        {price.prefix && (
          <span className="tmb-text-sm tmb-font-semibold tmb-leading-6 tmb-text-gray-600">{price.prefix}</span>
        )}
        <p className={cs("tmb-flex tmb-items-baseline tmb-gap-x-1", price.prefix ? "" : "tmb-mt-6")}>
          {isPaidPlan ? (
            <>
              <span className="tmb-text-3xl tmb-font-bold tmb-tracking-tight tmb-text-gray-900">
                ${price.value.toLocaleString("en-US")}
              </span>
              {price.suffix && (
                <span className="tmb-text-sm tmb-font-semibold tmb-leading-6 tmb-text-gray-600">{price.suffix}</span>
              )}
            </>
          ) : (
            <span className="tmb-text-3xl tmb-font-bold tmb-tracking-tight tmb-text-gray-900">Free</span>
          )}
        </p>
      </div>
      {isPaidPlan && (
        <a
          aria-describedby={product.name}
          className={cs(
            {
              "tmb-bg-mainBlue tmb-text-white tmb-shadow-sm hover:tmb-bg-darkBlue": highlighted,
              "tmb-text-gray tmb-ring-gray": isLoading,
              "tmb-text-mainBlue tmb-ring-1 tmb-ring-inset hover:tmb-ring-darkBlue hover:tmb-text-darkBlue":
                !isLoading && !highlighted,
            },
            "tmb-mt-6 tmb-block tmb-rounded-md tmb-py-2 tmb-px-3 tmb-text-center tmb-text-sm tmb-font-semibold tmb-leading-6 focus-visible:tmb-outline focus-visible:tmb-outline-2 focus-visible:tmb-outline-offset-2 focus-visible:tmb-outline-mainBlue tmb-select-none tmb-cursor-pointer",
            styles.buyButton
          )}
          onClick={() => selectPlan(product.prices[0]?.id)}
        >
          {isLoading ? <CircularProgress sx={{ marginBottom: -0.5 }} size="20px" /> : buyLabel}
        </a>
      )}
      <ul
        role="list"
        className="tmb-mt-8 tmb-space-y-3 tmb-text-sm tmb-leading-6 tmb-text-gray-600 xl:tmb-mt-10"
        style={{ padding: 0 }}
      >
        {product.features?.map((feature) => (
          <li key={feature} className="tmb-flex tmb-gap-x-3">
            <CheckIcon className="tmb-h-6 tmb-w-5 tmb-flex-none tmb-text-mainBlue" aria-hidden="true" />
            {feature?.split("(")[0]}
            {feature?.split("(")[1] ? (
              <>
                <br />({feature?.split("(")[1]}
              </>
            ) : (
              ""
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

interface PageProps {
  billingProducts: BillingProduct[]
  highlightedProduct?: GQL.AccountType
  showTitle?: boolean
  invitationText?: string | React.ReactNode
}

export const PricingPage: React.FC<PageProps> = ({
  billingProducts,
  highlightedProduct,
  showTitle = true,
  invitationText,
}) => {
  const sortedProducts = [...billingProducts].sort((p1, p2) => {
    if (!p1.prices?.[0]?.unitAmountCents) return -1
    if (!p2.prices?.[0]?.unitAmountCents) return 1
    return p1.prices[0].unitAmountCents - p2.prices[0].unitAmountCents
  })

  return (
    <div className="tmb-py-12 sm:tmb-py-10 tmb-font-sans" data-testid="pricing_plans">
      <div className="tmb-mx-auto tmb-px-6 lg:tmb-px-8 tmb-grid tmb-justify-center">
        {showTitle && (
          <div className="tmb-mx-auto tmb-max-w-4xl tmb-text-center">
            <p className={cs("tmb-mt-2 tmb-text-4xl tmb-font-bold tmb-tracking-tight sm:tmb-text-5xl tmb-mb-0")}>
              10x ROI Guarantee
            </p>
          </div>
        )}
        {invitationText && (
          <div className="tmb-mx-auto">
            <p>{invitationText || `Get 10x return on your investment using ${APP_DISPLAY_NAME} or your money back.`}</p>
          </div>
        )}
        <div className="tmb-flex tmb-mt-10">
          {sortedProducts.map((product) => (
            <BillingProductCard
              key={product.id}
              product={product}
              highlighted={product.accountType === highlightedProduct}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
